import { SELECTED_DASHBOARD_ID, SELECTED_OBJECT_ID, SELECTED_PERIOD, SELECTED_VIEW_ID, VIEW_COLUMNS_SIZING, VIEW_FOLDER_COLLAPSED } from "./constants";

// Browser-side settings of a grid, keyed by object and view (the grid without
// a view, the object configuration, has no view part).

// viewColumnSizingStorageId is the key of the persisted column widths.
export function viewColumnSizingStorageId(objectKey: string | null | undefined, viewKey: string | null | undefined): string {
    return VIEW_COLUMNS_SIZING + objectKey + (viewKey ? "-" + viewKey : "");
}

// viewPeriodStorageId is the key of the period selected for a date or period
// pivot.
export function viewPeriodStorageId(objectKey: string | null | undefined, viewKey: string | null | undefined): string {
    return SELECTED_PERIOD + objectKey + (viewKey ? "-" + viewKey : "");
}

// copyViewLocalSettings carries the browser-side settings of a view (column
// widths, selected pivot period) over to another view of the same object,
// typically its duplicate. A setting missing on the source leaves the target
// untouched.
export function copyViewLocalSettings(objectKey: string, sourceViewKey: string, targetViewKey: string): void {
    const storageIds = [
        [viewColumnSizingStorageId(objectKey, sourceViewKey), viewColumnSizingStorageId(objectKey, targetViewKey)],
        [viewPeriodStorageId(objectKey, sourceViewKey), viewPeriodStorageId(objectKey, targetViewKey)],
    ];
    storageIds.forEach(([sourceId, targetId]) => {
        const value = localStorage.getItem(sourceId);
        if (value !== null) {
            localStorage.setItem(targetId, value);
        }
    });
}

export function updateStorageSelection(workspaceKey: string | null | undefined, objectKey: string | null | undefined, viewKey: string | null | undefined, dashboardKey: string | null | undefined) {
    if (!workspaceKey) {
        return;
    }
    if (objectKey) {
        setSelectedObjectKey(workspaceKey, objectKey);
    } else {
        removeSelectedObjectKey(workspaceKey);
    }
    if (viewKey) {
        setSelectedViewKey(workspaceKey, viewKey);
    } else {
        removeSelectedViewKey(workspaceKey);
    }
    if (dashboardKey) {
        setSelectedDashboardKey(workspaceKey, dashboardKey);
    } else {
        removeSelectedDashboardKey(workspaceKey);
    }
}

export function getSelectedObjectStorageId(workspaceKey: string): string | null {
    if (!workspaceKey) {
        return null;
    }
    return SELECTED_OBJECT_ID + "-" + workspaceKey;
}

export function getSelectedObjectKey(workspaceKey: string): string | null {
    const storageId = getSelectedObjectStorageId(workspaceKey);
    if (!storageId) {
        return null;
    }
    return localStorage.getItem(storageId);
}

export function setSelectedObjectKey(workspaceKey: string, objectKey: string): void {
    const storageId = getSelectedObjectStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.setItem(storageId, objectKey);
}

export function removeSelectedObjectKey(workspaceKey: string): void {
    const storageId = getSelectedObjectStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.removeItem(storageId);
}


export function getSelectedViewStorageId(workspaceKey: string): string | null {
    if (!workspaceKey) {
        return null;
    }
    return SELECTED_VIEW_ID + "-" + workspaceKey;
}

export function getSelectedViewKey(workspaceKey: string): string | null {
    const storageId = getSelectedViewStorageId(workspaceKey);
    if (!storageId) {
        return null;
    }
    return localStorage.getItem(storageId);
}

export function setSelectedViewKey(workspaceKey: string, viewKey: string): void {
    const storageId = getSelectedViewStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.setItem(storageId, viewKey);
}

export function removeSelectedViewKey(workspaceKey: string): void { 
    const storageId = getSelectedViewStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.removeItem(storageId);
}

export function getSelectedDashboardStorageId(workspaceKey: string): string | null {
    if (!workspaceKey) {
        return null;
    }
    return SELECTED_DASHBOARD_ID + "-" + workspaceKey;
}

export function getSelectedDashboardKey(workspaceKey: string): string | null {
    const storageId = getSelectedDashboardStorageId(workspaceKey);
    if (!storageId) {
        return null;
    }
    return localStorage.getItem(storageId);
}

export function setSelectedDashboardKey(workspaceKey: string, dashboardKey: string): void {
    const storageId = getSelectedDashboardStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.setItem(storageId, dashboardKey);
}

export function removeSelectedDashboardKey(workspaceKey: string): void {
    const storageId = getSelectedDashboardStorageId(workspaceKey);
    if (!storageId) {
        return;
    }
    localStorage.removeItem(storageId);
}

// Collapsed state of the personal view folders (expanded by default).
// Keyed by workspace + object + folder path: the state resets when a folder
// is renamed or moved (its path changes), which is acceptable.
function viewFolderCollapsedStorageId(workspaceKey: string, objectKey: string, path: string): string {
    return VIEW_FOLDER_COLLAPSED + workspaceKey + "-" + objectKey + "-" + path;
}

export function isViewFolderCollapsed(workspaceKey: string | null | undefined, objectKey: string, path: string): boolean {
    if (!workspaceKey) {
        return false;
    }
    return localStorage.getItem(viewFolderCollapsedStorageId(workspaceKey, objectKey, path)) === "true";
}

export function setViewFolderCollapsed(workspaceKey: string | null | undefined, objectKey: string, path: string, collapsed: boolean): void {
    if (!workspaceKey) {
        return;
    }
    const storageId = viewFolderCollapsedStorageId(workspaceKey, objectKey, path);
    if (collapsed) {
        localStorage.setItem(storageId, "true");
    } else {
        localStorage.removeItem(storageId);
    }
}